// src/hooks/useTheme.ts
// Manages the app theme (light / dark / system / special).
// Writes "dark" and optionally "special" on <html> based on the preference.
// Special = warm charcoal/amber dark alternative; see docs/planning/special_dark_mode_plan.md.
import { useCallback, useEffect } from "react";
import { useLocalStorageState } from "./useLocalStorageState";
import { BB_THEME_STORAGE_KEY, patchUserUiPrefs } from "@/lib/userUiPrefs";

export type ThemePreference = "light" | "dark" | "system" | "special";

const VALID_PREFS: ReadonlySet<string> = new Set(["light", "dark", "system", "special"]);

function normalizePref(raw: unknown): ThemePreference {
  return typeof raw === "string" && VALID_PREFS.has(raw) ? (raw as ThemePreference) : "system";
}

function applyTheme(pref: ThemePreference) {
  const root = document.documentElement;
  const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isSpecial = pref === "special";
  const isDark = pref === "dark" || isSpecial || (pref === "system" && systemDark);
  root.classList.toggle("dark", isDark);
  root.classList.toggle("special", isSpecial);
}

export function useTheme(): [ThemePreference, (p: ThemePreference) => void] {
  const [stored, setStored] = useLocalStorageState<ThemePreference>(BB_THEME_STORAGE_KEY, "system");
  const pref = normalizePref(stored);

  const setTheme = useCallback(
    (p: ThemePreference) => {
      setStored(p);
      void patchUserUiPrefs({ theme: p });
    },
    [setStored],
  );

  // Apply on mount and whenever pref changes
  useEffect(() => {
    applyTheme(pref);

    // Re-evaluate "system" when OS preference changes
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => applyTheme("system");
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, [pref]);

  return [pref, setTheme];
}
